import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import {
  Accessibility,
  BookOpen,
  Brain,
  Ear,
  Eye,
  Glasses,
  Globe,
  Keyboard,
  Monitor,
  Palette,
  PersonStanding,
  RotateCcw,
  User,
  X,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PERSONA_DEFINITIONS } from '@/components/a11y/a11y-personas';
import {
  A11yCheckboxRow,
  A11yInfoCallout,
  A11yInfoRow,
  A11yPercentSlider,
  A11ySectionAccordion,
  A11ySegmentedControl,
  A11ySelectRow,
  A11yToggleRow,
} from '@/components/a11y/accessibility-menu-primitives';
import { SUPPORTED_LOCALE_CODES } from '@/i18n/supported-languages';
import {
  A11Y_FONT_SIZE_DEFAULT,
  A11Y_FONT_SIZE_MAX,
  A11Y_FONT_SIZE_MIN,
  A11Y_ZOOM_DEFAULT,
  A11Y_ZOOM_MAX,
  A11Y_ZOOM_MIN,
  useA11yPreferencesStore,
  useEffectiveA11yPreferences,
} from '@/stores/a11y-preferences-store';
import type { PersonaId } from '@/types/a11y-preferences';

const PERSONA_ICONS: Record<PersonaId, typeof Accessibility> = {
  adhdFriendly: Brain,
  astigmatism: Glasses,
  blindness: PersonStanding,
  colorBlind: Palette,
  dyslexiaFriendly: BookOpen,
  epilepsySafe: Zap,
  olderUsers: User,
  lowVision: Eye,
  motorDisabilities: Accessibility,
  seizureSafe: Brain,
};

function focusMainContent() {
  const main = document.getElementById('main-content');
  if (main) {
    main.focus();
    main.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function PersonasSection() {
  const { t } = useTranslation('settings');
  const manual = useA11yPreferencesStore((s) => s.manual);
  const setPersona = useA11yPreferencesStore((s) => s.setPersona);

  return (
    <A11ySectionAccordion id="a11y-personas" title={t('accessibility.personas.title')} defaultOpen>
      <A11yInfoCallout>{t('accessibility.personas.intro')}</A11yInfoCallout>
      <ul className="divide-y divide-border/60">
        {PERSONA_DEFINITIONS.map((persona) => {
          const Icon = PERSONA_ICONS[persona.id];
          const enabled = Boolean(manual.activePersonas[persona.id]);
          return (
            <li key={persona.id} className="flex items-center gap-3 py-3">
              <Icon size={18} className="shrink-0 text-muted-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{t(persona.labelKey)}</p>
                <p className="text-xs text-muted-foreground">{t(persona.descriptionKey)}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={t(persona.labelKey)}
                onClick={() => setPersona(persona.id, !enabled)}
                className={clsx(
                  'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent',
                  enabled ? 'bg-primary' : 'bg-muted-foreground/30',
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-4 w-4 rounded-full bg-background shadow transition-transform',
                    enabled ? 'translate-x-4' : 'translate-x-0',
                  )}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </A11ySectionAccordion>
  );
}

function VisionSection() {
  const { t } = useTranslation('settings');
  const effective = useEffectiveA11yPreferences();
  const patchManual = useA11yPreferencesStore((s) => s.patchManual);
  const setFontSizePercent = useA11yPreferencesStore((s) => s.setFontSizePercent);
  const resetFontSizePercent = useA11yPreferencesStore((s) => s.resetFontSizePercent);
  const setZoomPercent = useA11yPreferencesStore((s) => s.setZoomPercent);
  const resetZoomPercent = useA11yPreferencesStore((s) => s.resetZoomPercent);

  return (
    <A11ySectionAccordion
      id="a11y-vision"
      icon={<Eye size={16} aria-hidden />}
      title={t('accessibility.vision.title')}
    >
      <A11yPercentSlider
        label={t('accessibility.fontSize.label', { percent: effective.fontSizePercent })}
        description={t('accessibility.fontSize.description')}
        value={effective.fontSizePercent}
        min={A11Y_FONT_SIZE_MIN}
        max={A11Y_FONT_SIZE_MAX}
        onChange={setFontSizePercent}
        onReset={effective.fontSizePercent !== A11Y_FONT_SIZE_DEFAULT ? resetFontSizePercent : undefined}
        resetLabel={t('accessibility.fontSize.reset')}
        decreaseAria={t('accessibility.fontSize.decrease')}
        increaseAria={t('accessibility.fontSize.increase')}
        sliderAria={t('accessibility.fontSize.sliderAria')}
      />
      {!effective.adaptFontToZoom ? (
        <A11yPercentSlider
          label={t('accessibility.zoom.label', { percent: effective.zoomPercent })}
          description={t('accessibility.zoom.description')}
          value={effective.zoomPercent}
          min={A11Y_ZOOM_MIN}
          max={A11Y_ZOOM_MAX}
          onChange={setZoomPercent}
          onReset={effective.zoomPercent !== A11Y_ZOOM_DEFAULT ? resetZoomPercent : undefined}
          resetLabel={t('accessibility.zoom.reset')}
          decreaseAria={t('accessibility.zoom.decrease')}
          increaseAria={t('accessibility.zoom.increase')}
          sliderAria={t('accessibility.zoom.sliderAria')}
          showPreview={false}
        />
      ) : null}
      <A11yCheckboxRow
        id="a11y-adapt-font-zoom"
        label={t('accessibility.zoom.adaptFont')}
        checked={effective.adaptFontToZoom}
        onCheckedChange={(checked) => patchManual({ adaptFontToZoom: checked })}
      />
      <A11yToggleRow
        id="a11y-high-contrast"
        label={t('accessibility.highContrast.label')}
        description={t('accessibility.highContrast.description')}
        checked={effective.highContrast}
        onCheckedChange={(checked) => patchManual({ highContrast: checked })}
        preview={<div className="h-8 w-8 rounded-full border-2 border-foreground bg-background" />}
      />
      <A11yToggleRow
        id="a11y-monochrome"
        label={t('accessibility.monochrome.label')}
        description={t('accessibility.monochrome.description')}
        checked={effective.monochrome}
        onCheckedChange={(checked) => patchManual({ monochrome: checked })}
        preview={<div className="h-8 w-full rounded bg-gradient-to-r from-black to-white" />}
      />
      <A11yToggleRow
        id="a11y-reading-mask"
        label={t('accessibility.readingMask.label')}
        description={t('accessibility.readingMask.description')}
        checked={effective.readingMask}
        onCheckedChange={(checked) => patchManual({ readingMask: checked })}
        preview={<div className="h-8 w-full rounded bg-foreground/80" style={{ boxShadow: 'inset 0 12px 0 0 white, inset 0 20px 0 0 transparent' }} />}
      />
      <A11yToggleRow
        id="a11y-focus-ring"
        label={t('accessibility.focusRing.label')}
        description={t('accessibility.focusRing.description')}
        checked={effective.focusRingEmphasis}
        onCheckedChange={(checked) => patchManual({ focusRingEmphasis: checked })}
        badge={t('accessibility.beta')}
        preview={<span className="rounded border-2 border-primary px-1 text-xs font-bold">Abc</span>}
      />
      <A11yToggleRow
        id="a11y-underline-links"
        label={t('accessibility.underlineLinks.label')}
        description={t('accessibility.underlineLinks.description')}
        checked={effective.underlineLinks}
        onCheckedChange={(checked) => patchManual({ underlineLinks: checked })}
        preview={<span className="text-xs font-bold underline">Abc</span>}
      />
      <A11yToggleRow
        id="a11y-toggle-labels"
        label={t('accessibility.toggleLabels.label')}
        description={t('accessibility.toggleLabels.description')}
        checked={effective.toggleOnOffLabels}
        onCheckedChange={(checked) => patchManual({ toggleOnOffLabels: checked })}
      />
      <A11ySegmentedControl
        label={t('accessibility.requiredField.label')}
        description={t('accessibility.requiredField.description')}
        value={effective.requiredFieldStyle}
        options={[
          { value: 'asterisk' as const, label: t('accessibility.requiredField.asterisk') },
          { value: 'text' as const, label: t('accessibility.requiredField.text') },
        ]}
        onChange={(value) => patchManual({ requiredFieldStyle: value })}
      />
      <A11yCheckboxRow
        id="a11y-required-red"
        label={t('accessibility.requiredField.redLabel')}
        checked={effective.requiredFieldsRed}
        onCheckedChange={(checked) => patchManual({ requiredFieldsRed: checked })}
      />
      <A11yToggleRow
        id="a11y-strikethrough-disabled"
        label={t('accessibility.strikethroughDisabled.label')}
        description={t('accessibility.strikethroughDisabled.description')}
        checked={effective.strikethroughDisabled}
        onCheckedChange={(checked) => patchManual({ strikethroughDisabled: checked })}
        preview={<span className="text-xs line-through opacity-60">Btn</span>}
      />
      <A11yToggleRow
        id="a11y-dyslexia-font"
        label={t('accessibility.dyslexiaFont.label')}
        description={t('accessibility.dyslexiaFont.description')}
        checked={effective.dyslexiaFont}
        onCheckedChange={(checked) => patchManual({ dyslexiaFont: checked })}
        preview={<span className="font-serif text-xs">Abc</span>}
      />
      <A11ySegmentedControl
        label={t('accessibility.picklistStyle.label')}
        description={t('accessibility.picklistStyle.description')}
        value={effective.picklistStyle}
        options={[
          { value: 'bold' as const, label: t('accessibility.picklistStyle.bold') },
          { value: 'subtle' as const, label: t('accessibility.picklistStyle.subtle') },
          { value: 'minimal' as const, label: t('accessibility.picklistStyle.minimal') },
        ]}
        onChange={(value) => patchManual({ picklistStyle: value })}
      />
    </A11ySectionAccordion>
  );
}

function HearingSection() {
  const { t } = useTranslation('settings');

  return (
    <A11ySectionAccordion
      id="a11y-hearing"
      icon={<Ear size={16} aria-hidden />}
      title={t('accessibility.hearing.title')}
    >
      <A11yInfoRow
        label={t('accessibility.hearing.screenReader.label')}
        description={t('accessibility.hearing.screenReader.description')}
        preview={<Monitor size={20} className="text-muted-foreground" />}
      />
    </A11ySectionAccordion>
  );
}

function MobilitySection() {
  const { t } = useTranslation('settings');
  const effective = useEffectiveA11yPreferences();
  const patchManual = useA11yPreferencesStore((s) => s.patchManual);
  const setMenuOpen = useA11yPreferencesStore((s) => s.setMenuOpen);

  return (
    <A11ySectionAccordion
      id="a11y-mobility"
      icon={<Accessibility size={16} aria-hidden />}
      title={t('accessibility.mobility.title')}
    >
      <A11yInfoRow
        label={t('accessibility.mobility.keyboardShortcuts.label')}
        description={t('accessibility.mobility.keyboardShortcuts.description')}
        action={
          <Link
            to="/settings/profile"
            className="inline-flex rounded-lg border border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
            onClick={() => setMenuOpen(false)}
          >
            {t('accessibility.mobility.keyboardShortcuts.action')}
          </Link>
        }
        preview={<Keyboard size={20} className="text-muted-foreground" />}
      />
      <A11yInfoRow
        label={t('accessibility.navigation.skipToMainTitle')}
        description={t('accessibility.navigation.skipToMain')}
        action={
          <button
            type="button"
            onClick={focusMainContent}
            className="inline-flex rounded-lg border border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
          >
            {t('accessibility.navigation.skipLinkAction')}
          </button>
        }
      />
      <A11yToggleRow
        id="a11y-custom-cursor"
        label={t('accessibility.customCursor.label')}
        description={t('accessibility.customCursor.description')}
        checked={effective.customCursor}
        onCheckedChange={(checked) => patchManual({ customCursor: checked })}
      />
      <A11yToggleRow
        id="a11y-custom-scrollbar"
        label={t('accessibility.customScrollbar.label')}
        description={t('accessibility.customScrollbar.description')}
        checked={effective.customScrollbar}
        onCheckedChange={(checked) => patchManual({ customScrollbar: checked })}
      />
      <A11ySelectRow
        id="a11y-display-layout"
        label={t('accessibility.mobility.displayLayout.label')}
        description={t('accessibility.mobility.displayLayout.description')}
        value={effective.displayLayout}
        options={[
          { value: 'adaptive', label: t('accessibility.mobility.displayLayout.adaptive') },
          { value: 'compact', label: t('accessibility.mobility.displayLayout.compact') },
          { value: 'comfortable', label: t('accessibility.mobility.displayLayout.comfortable') },
        ]}
        onChange={(value) =>
          patchManual({ displayLayout: value as 'adaptive' | 'compact' | 'comfortable' })
        }
      />
    </A11ySectionAccordion>
  );
}

function LearningSection() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const effective = useEffectiveA11yPreferences();
  const patchManual = useA11yPreferencesStore((s) => s.patchManual);

  return (
    <A11ySectionAccordion
      id="a11y-learning"
      icon={<BookOpen size={16} aria-hidden />}
      title={t('accessibility.learning.title')}
    >
      <A11ySelectRow
        id="a11y-reduced-motion"
        label={t('accessibility.reducedMotion.label')}
        description={t('accessibility.reducedMotion.description')}
        value={effective.reducedMotion}
        options={[
          { value: 'system', label: t('accessibility.reducedMotion.system') },
          { value: 'on', label: t('accessibility.reducedMotion.on') },
          { value: 'off', label: t('accessibility.reducedMotion.off') },
        ]}
        onChange={(value) => patchManual({ reducedMotion: value as 'system' | 'on' | 'off' })}
      />
      <A11yToggleRow
        id="a11y-highlight-critical"
        label={t('accessibility.highlightCritical.label')}
        description={t('accessibility.highlightCritical.description')}
        checked={effective.highlightCritical}
        onCheckedChange={(checked) => patchManual({ highlightCritical: checked })}
        preview={<span className="text-xs font-bold text-primary">Abc</span>}
      />
      <A11yInfoRow
        label={t('accessibility.learning.language.label')}
        description={t('accessibility.learning.language.description')}
        action={
          <Link
            to="/settings/profile"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
          >
            <Globe size={12} aria-hidden />
            {tc(`common.language.${SUPPORTED_LOCALE_CODES[0]}`)} — {t('accessibility.learning.language.action')}
          </Link>
        }
        preview={<Globe size={20} className="text-muted-foreground" />}
      />
    </A11ySectionAccordion>
  );
}

export function AccessibilityMenu() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const menuOpen = useA11yPreferencesStore((s) => s.menuOpen);
  const setMenuOpen = useA11yPreferencesStore((s) => s.setMenuOpen);
  const resetAll = useA11yPreferencesStore((s) => s.resetAll);

  return (
    <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={clsx(
            'fixed top-0 right-0 bottom-0 z-[201] flex w-[min(440px,100vw)] flex-col border-l border-border bg-background shadow-modal outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          )}
          aria-describedby="a11y-menu-description"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
            <Accessibility size={18} className="text-primary" aria-hidden />
            <Dialog.Title className="text-base font-semibold tracking-tight">
              {t('accessibility.title')}
            </Dialog.Title>
            <Dialog.Close
              type="button"
              className="ml-auto rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={tc('shared.mobileDrawer.close')}
            >
              <X size={20} />
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
            <p
              id="a11y-menu-description"
              className="mb-2 rounded-lg bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground"
            >
              {t('accessibility.intro')}
            </p>
            <PersonasSection />
            <VisionSection />
            <HearingSection />
            <MobilitySection />
            <LearningSection />
          </div>

          <div className="shrink-0 border-t border-border px-4 py-3">
            <button
              type="button"
              onClick={resetAll}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
            >
              <RotateCcw size={14} aria-hidden />
              {t('accessibility.resetAll')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
